import React, {useMemo, useState, useEffect} from 'react';
import {Alert, Box, DialogContentText, Divider, InputAdornment, Stack, Switch, Typography} from '@mui/material';
import PropTypes from 'prop-types';
import {validateEmail, validateFieldLength, validateMultipleEmails} from '../../../utility/validationUtil';
import AdmicityForm from '../../../shared-components/AdmicityForm';
import AdmicityDialog from '../../../shared-components/AdmicityDialog';
import AdmicityCronScheduler from '../../../shared-components/AdmicityCronScheduler';
import LogoUploader from './LogoUploader';
import useUser from '../../../utility/hooks/useUser';
import {ROLES} from '../../../constants/roles';
import StudentParentInvitationsSettings from './StudentParentInvitationsSettings';
import {PARENT_INVITATION_TARGET_MODES} from '../../../constants/parentInvitationTargetModes';

const validateDFENumber = (value) => {
    const isValid = /^\d{3}\d{4}$/.test(value);
    return {
        isValid,
        message: isValid ? '' : 'DFE number must be in the format: 3 digits for authority and 4 digits for school number',
    };
};

const validateInterval = (value) => {
    const numericValue = +value;
    const isValid = Number.isInteger(numericValue) && numericValue >= 0 && numericValue <= 1000;

    return {
        isValid,
        message: isValid ? '' : 'The value must be a whole number between 0 and 1000. Enter 0 to disable.',
    };
};

const validateWebDomain = (value) => {
    const isValid = /^([a-zA-Z0-9][a-zA-Z0-9-_]*\.)*[a-zA-Z0-9]*[a-zA-Z0-9-_]*[a-zA-Z0-9]+$/.test(value) &&
        !value.includes('admicity.co.uk');

    return {
        isValid,
        message: isValid ? '' : 'Enter a valid domain (e.g., hogwarts) without ".admicity.co.uk"',
    };
};

const normaliseIds = (ids) => (ids ?? [])
    .filter(id => id > 0)
    .slice()
    .sort((left, right) => left - right);

const areNumberArraysEqual = (left, right) =>
    left.length === right.length &&
    left.every((value, index) => value === right[index]);

const settingsSectionSx = {
    border: '1px solid rgba(0, 0, 0, 0.12)',
    borderRadius: 1,
    p: 2,
    mt: 2,
    mb: 1
};

const getItemChecked = (item, value) => {
    const rawValue = value?.[item.name] ?? false;
    return item.invertChecked ? !rawValue : rawValue;
};

const SettingsToggleRow = ({item, checked, onChange}) => (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <Box>
            <Typography fontWeight={600}>{item.label}</Typography>
            {item.description && (
                <Typography variant="body2" color="text.secondary">
                    {item.description}
                </Typography>
            )}
        </Box>
        <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end'}}>
            <Switch
                checked={checked}
                onChange={(event) => onChange(event.target.checked)}
            />
        </Box>
    </Stack>
);

const SettingsToggleSection = ({title, items, value, onChange}) => (
    <Box sx={{mt: 2, mb: 1}}>
        {title && (
            <Typography variant="h6" fontWeight="bold" sx={{mb: 1}}>
                {title}
            </Typography>
        )}
        <Box sx={settingsSectionSx}>
            <Stack
                spacing={2}
                divider={<Divider flexItem />}
            >
                {items.map(item => (
                    <Box key={item.name}>
                        <SettingsToggleRow
                            item={item}
                            checked={getItemChecked(item, value)}
                            onChange={(nextChecked) => onChange({
                                ...value,
                                [item.name]: item.invertChecked ? !nextChecked : nextChecked
                            })}
                        />
                    </Box>
                ))}
            </Stack>
        </Box>
    </Box>
);

SettingsToggleRow.propTypes = {
    item: PropTypes.shape({
        label: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        description: PropTypes.string,
        invertChecked: PropTypes.bool
    }).isRequired,
    checked: PropTypes.bool,
    onChange: PropTypes.func.isRequired
};

SettingsToggleSection.propTypes = {
    title: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        description: PropTypes.string,
        invertChecked: PropTypes.bool
    })).isRequired,
    value: PropTypes.object,
    onChange: PropTypes.func.isRequired
};

const SchoolSetupForm = (
    {
        editMode,
        schoolInfo,
        onSubmit,
        isLoading,
        logo
    }) => {
    const [showAlert, setShowAlert] = useState(false);
    const [isFormValid, setIsFormValid] = useState(true);
    const [cronExpression, setCronExpression] = useState('');
    const [photoCronExpression, setPhotoCronExpression] = useState('');
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingSubmission, setPendingSubmission] = useState(null);
    const [pendingShowAlert, setPendingShowAlert] = useState(false);
    const [pendingInvitationWarning, setPendingInvitationWarning] = useState(false);
    const [pendingEnabledInvitationLabels, setPendingEnabledInvitationLabels] = useState([]);
    const {user} = useUser();

    const getParentInvitationSettings = (source) => ({
        disableParentInvitations: source.disableParentInvitations ?? true,
        parentInvitationTargetMode: source.parentInvitationTargetMode ??
            PARENT_INVITATION_TARGET_MODES.selectedYearGroups,
        parentInvitationYearGroupIds: source.parentInvitationYearGroupIds ?? []
    });
    const getGroupSettings = (source) => ({
        disableEnquiriesSync: source.disableEnquiriesSync ?? false,
        hideBoarderGroups: source.hideBoarderGroups ?? false,
        hideHouseGroups: source.hideHouseGroups ?? false
    });
    const getInvitationSettings = (source) => ({
        disableApplicationParentInvitations: source.disableApplicationParentInvitations ?? false,
        disableStudentInvitations: source.disableStudentInvitations ?? false,
        disableEmployeeInvitations: source.disableEmployeeInvitations ?? false
    });
    const getNotificationSettings = (source) => ({
        disableParentNotificationApplicationUpdates: source.disableParentNotificationApplicationUpdates ?? false,
        disableParentNotificationStudentUpdates: source.disableParentNotificationStudentUpdates ?? false
    });
    const getMfaSettings = (source) => ({
        mfaEnabled: source.mfaEnabled ?? false,
        mfaDeviceTrustDays: source.mfaDeviceTrustDays ?? 30
    });
    const getMedicineSettings = (source) => ({
        medicineExpiryWarningDays: source.medicineExpiryWarningDays ?? 28
    });
    const [parentInvitationSettings, setParentInvitationSettings] = useState(getParentInvitationSettings(schoolInfo));
    const [groupSettings, setGroupSettings] = useState(getGroupSettings(schoolInfo));
    const [invitationSettings, setInvitationSettings] = useState(getInvitationSettings(schoolInfo));
    const [notificationSettings, setNotificationSettings] = useState(getNotificationSettings(schoolInfo));
    const [mfaSettings, setMfaSettings] = useState(getMfaSettings(schoolInfo));
    const [medicineSettings, setMedicineSettings] = useState(getMedicineSettings(schoolInfo));
    const [currentLogo, setCurrentLogo] = useState(logo);

    useEffect(() => {
        setParentInvitationSettings(getParentInvitationSettings(schoolInfo));
    }, [
        schoolInfo.disableParentInvitations,
        schoolInfo.parentInvitationTargetMode,
        schoolInfo.parentInvitationYearGroupIds
    ]);

    useEffect(() => {
        setCurrentLogo(logo);
    }, [logo]);

    useEffect(() => {
        setGroupSettings(getGroupSettings(schoolInfo));
        setInvitationSettings(getInvitationSettings(schoolInfo));
        setNotificationSettings(getNotificationSettings(schoolInfo));
        setMfaSettings(getMfaSettings(schoolInfo));
        setMedicineSettings(getMedicineSettings(schoolInfo));
    }, [
        schoolInfo.disableEnquiriesSync,
        schoolInfo.hideBoarderGroups,
        schoolInfo.hideHouseGroups,
        schoolInfo.disableApplicationParentInvitations,
        schoolInfo.disableStudentInvitations,
        schoolInfo.disableEmployeeInvitations,
        schoolInfo.disableParentNotificationApplicationUpdates,
        schoolInfo.disableParentNotificationStudentUpdates,
        schoolInfo.mfaEnabled,
        schoolInfo.mfaDeviceTrustDays,
        schoolInfo.medicineExpiryWarningDays
    ]);

    useEffect(() => {
        const validateForm = () => {
            const fieldsToValidate = [
                { name: 'name', value: schoolInfo.name || '', required: true, validate: (value) => validateFieldLength(value, 400) },
                { name: 'dfeNumber', value: schoolInfo.dfeNumber || '', required: true, validate: validateDFENumber },
                { name: 'webDomain', value: schoolInfo.domain || '', required: true, validate: validateWebDomain },
                { name: 'emailAddresses', value: schoolInfo.emailAddresses || '', required: false, validate: validateMultipleEmails },
                { name: 'notes', value: schoolInfo.notes || '', required: false, validate: () => ({ isValid: true }) }
            ];

            const hasErrors = fieldsToValidate.some(field => {
                if (!field.required && (!field.value || field.value === '')) {
                    return false;
                }
                const validation = field.validate(field.value);
                return !validation.isValid;
            });

            setIsFormValid(!hasErrors);
        };

        validateForm();
    }, [schoolInfo]);

    const shouldWarnAboutInvitationSend = (body) => {
        const currentParentSettings = getParentInvitationSettings(schoolInfo);
        const nextParentSettings = {
            disableParentInvitations: body.disableParentInvitations,
            parentInvitationTargetMode: body.parentInvitationTargetMode,
            parentInvitationYearGroupIds: normaliseIds(body.parentInvitationYearGroupIds)
        };

        const currentParentYearGroupIds = normaliseIds(currentParentSettings.parentInvitationYearGroupIds);
        const isParentInvitationTargetingChanged =
            currentParentSettings.parentInvitationTargetMode !== nextParentSettings.parentInvitationTargetMode ||
            !areNumberArraysEqual(currentParentYearGroupIds, nextParentSettings.parentInvitationYearGroupIds);

        const willSendParentInvitations =
            (schoolInfo.disableParentInvitations && !body.disableParentInvitations) ||
            (isParentInvitationTargetingChanged && !body.disableParentInvitations);

        return willSendParentInvitations ||
            (schoolInfo.disableApplicationParentInvitations && !body.disableApplicationParentInvitations) ||
            (schoolInfo.disableStudentInvitations && !body.disableStudentInvitations) ||
            (schoolInfo.disableEmployeeInvitations && !body.disableEmployeeInvitations);
    };

    const getEnabledInvitationLabels = (body) => {
        const labels = [];
        const currentParentSettings = getParentInvitationSettings(schoolInfo);
        const nextParentSettings = {
            disableParentInvitations: body.disableParentInvitations,
            parentInvitationTargetMode: body.parentInvitationTargetMode,
            parentInvitationYearGroupIds: normaliseIds(body.parentInvitationYearGroupIds)
        };
        const currentParentYearGroupIds = normaliseIds(currentParentSettings.parentInvitationYearGroupIds);
        const isParentInvitationTargetingChanged =
            currentParentSettings.parentInvitationTargetMode !== nextParentSettings.parentInvitationTargetMode ||
            !areNumberArraysEqual(currentParentYearGroupIds, nextParentSettings.parentInvitationYearGroupIds);

        if (schoolInfo.disableApplicationParentInvitations && !body.disableApplicationParentInvitations) {
            labels.push('Applicant\'s Parent Invitations');
        }

        if (
            (schoolInfo.disableParentInvitations && !body.disableParentInvitations) ||
            (isParentInvitationTargetingChanged && !body.disableParentInvitations)
        ) {
            labels.push('Student\'s Parent Invitations');
        }

        if (schoolInfo.disableStudentInvitations && !body.disableStudentInvitations) {
            labels.push('Student Invitations');
        }

        if (schoolInfo.disableEmployeeInvitations && !body.disableEmployeeInvitations) {
            labels.push('Employee Invitations');
        }

        return labels;
    };

    const handleSubmit = (payload) => {
        const nextParentInvitationSettings = payload.parentInvitationSettings ??
            parentInvitationSettings;
        const nextGroupSettings = payload.groupSettings ?? getGroupSettings(schoolInfo);
        const nextInvitationSettings = payload.invitationSettings ?? getInvitationSettings(schoolInfo);
        const nextNotificationSettings = payload.notificationSettings ?? getNotificationSettings(schoolInfo);
        const nextMfaSettings = payload.mfaSettings ?? getMfaSettings(schoolInfo);
        const nextMedicineSettings = payload.medicineSettings ?? getMedicineSettings(schoolInfo);
        const body =
            !editMode
                ? {
                    name: payload.name,
                    dfeNumber: payload.dfeNumber,
                    subDomain: payload.domain,
                    domain: process.env.REACT_APP_DOMAIN,
                    notes: payload.notes,
                    registrationInviteConfigOption: payload.registrationInviteConfigOption,
                    emailAddresses: payload.emailAddresses,
                    noAccountAutoReminderInterval: payload.noAccountAutoReminderInterval,
                    unconfirmedApplicationReminderIntervalInHours: payload.unconfirmedApplicationReminderIntervalInHours,
                    unconfirmedStudentReminderIntervalInHours: payload.unconfirmedStudentReminderIntervalInHours,
                    syncInterval: cronExpression,
                    syncPhotoInterval: photoCronExpression,
                    hideBoarderGroups: nextGroupSettings.hideBoarderGroups,
                    hideHouseGroups: nextGroupSettings.hideHouseGroups,
                    disableParentInvitations: nextParentInvitationSettings.disableParentInvitations,
                    parentInvitationTargetMode: nextParentInvitationSettings.parentInvitationTargetMode,
                    parentInvitationYearGroupIds: nextParentInvitationSettings.parentInvitationYearGroupIds,
                    disableApplicationParentInvitations: nextInvitationSettings.disableApplicationParentInvitations,
                    disableStudentInvitations: nextInvitationSettings.disableStudentInvitations,
                    disableEmployeeInvitations: nextInvitationSettings.disableEmployeeInvitations,
                    disableParentNotificationApplicationUpdates: nextNotificationSettings.disableParentNotificationApplicationUpdates,
                    disableParentNotificationStudentUpdates: nextNotificationSettings.disableParentNotificationStudentUpdates,
                    disableEnquiriesSync: nextGroupSettings.disableEnquiriesSync,
                    mfaEnabled: nextMfaSettings.mfaEnabled,
                    mfaDeviceTrustDays: Number(nextMfaSettings.mfaDeviceTrustDays) || 0,
                    medicineExpiryWarningDays: Number(nextMedicineSettings.medicineExpiryWarningDays) || 28,
                    logo: payload.logo,
                    enquiryResponseEmail: payload.enquiryResponseEmail
                }
                : {
                    id: schoolInfo.id,
                    name: payload.name,
                    dfeNumber: payload.dfeNumber,
                    notes: payload.notes,
                    registrationInviteConfigOption: payload.registrationInviteConfigOption,
                    emailAddresses: payload.emailAddresses,
                    noAccountAutoReminderInterval: payload.noAccountAutoReminderInterval,
                    unconfirmedApplicationReminderIntervalInHours: payload.unconfirmedApplicationReminderIntervalInHours,
                    unconfirmedStudentReminderIntervalInHours: payload.unconfirmedStudentReminderIntervalInHours,
                    syncInterval: cronExpression,
                    syncPhotoInterval: photoCronExpression,
                    hideBoarderGroups: nextGroupSettings.hideBoarderGroups,
                    hideHouseGroups: nextGroupSettings.hideHouseGroups,
                    disableParentInvitations: nextParentInvitationSettings.disableParentInvitations,
                    parentInvitationTargetMode: nextParentInvitationSettings.parentInvitationTargetMode,
                    parentInvitationYearGroupIds: nextParentInvitationSettings.parentInvitationYearGroupIds,
                    disableApplicationParentInvitations: nextInvitationSettings.disableApplicationParentInvitations,
                    disableStudentInvitations: nextInvitationSettings.disableStudentInvitations,
                    disableEmployeeInvitations: nextInvitationSettings.disableEmployeeInvitations,
                    disableParentNotificationApplicationUpdates: nextNotificationSettings.disableParentNotificationApplicationUpdates,
                    disableParentNotificationStudentUpdates: nextNotificationSettings.disableParentNotificationStudentUpdates,
                    disableEnquiriesSync: nextGroupSettings.disableEnquiriesSync,
                    mfaEnabled: nextMfaSettings.mfaEnabled,
                    mfaDeviceTrustDays: Number(nextMfaSettings.mfaDeviceTrustDays) || 0,
                    medicineExpiryWarningDays: Number(nextMedicineSettings.medicineExpiryWarningDays) || 28,
                    logo: payload.logo,
                    enquiryResponseEmail: payload.enquiryResponseEmail
                };

        setPendingShowAlert(
            schoolInfo.hideBoarderGroups !== nextGroupSettings.hideBoarderGroups ||
            schoolInfo.hideHouseGroups !== nextGroupSettings.hideHouseGroups
        );
        const shouldWarn = shouldWarnAboutInvitationSend(body);
        setPendingInvitationWarning(shouldWarn);
        setPendingEnabledInvitationLabels(shouldWarn ? getEnabledInvitationLabels(body) : []);
        setPendingSubmission(body);
        setConfirmOpen(true);
    };

    const handleConfirmSave = () => {
        if (!pendingSubmission) {
            return;
        }

        setShowAlert(pendingShowAlert);
        onSubmit(pendingSubmission);
        setConfirmOpen(false);
        setPendingSubmission(null);
        setPendingShowAlert(false);
        setPendingInvitationWarning(false);
        setPendingEnabledInvitationLabels([]);
    };

    const handleCancelSave = () => {
        setConfirmOpen(false);
        setPendingSubmission(null);
        setPendingShowAlert(false);
        setPendingInvitationWarning(false);
        setPendingEnabledInvitationLabels([]);
    };

    const textFields = useMemo(() => [
                {
                    label: 'School Name',
                    name: 'name',
                    initialValue: schoolInfo.name,
                    validate: (value) => validateFieldLength(value, 400)
                },
                {
                    label: 'DFE Number',
                    name: 'dfeNumber',
                    initialValue: schoolInfo.dfeNumber,
                    validate: validateDFENumber
                },
                {
                    label: 'School Web Domain at Admicity',
                    name: 'domain',
                    initialValue: schoolInfo.domain,
                    helperText: 'Enter a domain (e.g., hogwarts.admicity.co.uk)',
                    disabled: editMode,
                    InputProps: (!editMode
                        ? {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Typography>{process.env.REACT_APP_DOMAIN}</Typography>
                                </InputAdornment>
                            ),
                        }
                        : {}),
                    validate: validateWebDomain
                },
                {
                    label: 'Email addresses for profile change notifications (comma-separated)',
                    name: 'emailAddresses',
                    required: false,
                    initialValue: schoolInfo.emailAddresses,
                    validate: validateMultipleEmails,
                    helperText: 'Enter comma-separated email addresses (e.g., admin@school.com, notifications@school.com)'
                },
                {
                    label: 'Auto Reminder (hours) – Unregistered Contacts',
                    hint: 'Set to 0 to disable automatic reminders.',
                    name: 'noAccountAutoReminderInterval',
                    required: true,
                    initialValue: schoolInfo.noAccountAutoReminderInterval,
                    validate: validateInterval
                },
                {
                    label: 'Auto Reminder (hours) – Unconfirmed Applications',
                    hint: 'Set to 0 to disable automatic reminders.',
                    name: 'unconfirmedApplicationReminderIntervalInHours',
                    required: true,
                    initialValue: schoolInfo.unconfirmedApplicationReminderIntervalInHours,
                    validate: validateInterval
                },
                {
                    label: 'Auto Reminder (hours) – Unconfirmed Students',
                    hint: 'Set to 0 to disable automatic reminders.',
                    name: 'unconfirmedStudentReminderIntervalInHours',
                    required: true,
                    initialValue: schoolInfo.unconfirmedStudentReminderIntervalInHours,
                    validate: validateInterval
                },
        !schoolInfo.disableEnquiriesSync && {
                    label: 'Enquiry form notification email',
                    name: 'enquiryResponseEmail',
                    type: 'email',
                    initialValue: schoolInfo.enquiryResponseEmail,
                    required: false,
                    validate: validateEmail,
                    nullableOnEmpty: true
                },
                {
                    label: 'Notes',
                    name: 'notes',
                    initialValue: schoolInfo.notes,
                    multiline: true,
                    rows: 4,
                    required: false
                }
    ].filter(Boolean), [schoolInfo, editMode]);

    const toggleSections = useMemo(() => [
        {
            name: 'groupSettings',
            initialValue: groupSettings,
            render: props => {
                // eslint-disable-next-line react/prop-types
                const {onChange, value} = props;

                return <SettingsToggleSection
                    key="groupSettings"
                    title="Groups"
                    value={value}
                    onChange={(nextValue) => {
                        setGroupSettings(nextValue);
                        onChange(nextValue);
                    }}
                    items={[
                        ...(user.hasRole(ROLES.SUPER_ADMIN)
                            ? [{
                                label: 'Disable Enquiries Synchronization',
                                name: 'disableEnquiriesSync'
                            }]
                            : []),
                        {
                            label: 'Hide Boarder Groups',
                            name: 'hideBoarderGroups'
                        },
                        {
                            label: 'Hide House Groups',
                            name: 'hideHouseGroups'
                        }
                    ]}
                />;
            }
        },
        {
            name: 'invitationSettings',
            initialValue: invitationSettings,
            render: props => {
                // eslint-disable-next-line react/prop-types
                const {onChange, value} = props;

                return <Box key="invitationSettings" sx={{mt: 2, mb: 1}}>
                    <Typography variant="h6" fontWeight="bold" sx={{mb: 1}}>
                        Account Registration Invitations
                    </Typography>
                    <Box sx={settingsSectionSx}>
                        <Stack spacing={2} divider={<Divider flexItem />}>
                            <Box>
                                <SettingsToggleRow
                                    item={{
                                        label: 'Disable Application Parent Invitations',
                                        name: 'disableApplicationParentInvitations'
                                    }}
                                    checked={getItemChecked({
                                        name: 'disableApplicationParentInvitations'
                                    }, value)}
                                    onChange={(nextChecked) => {
                                        const nextValue = {
                                            ...value,
                                            disableApplicationParentInvitations: nextChecked
                                        };
                                        setInvitationSettings(nextValue);
                                        onChange(nextValue);
                                    }}
                                />
                            </Box>
                            <Box>
                                <SettingsToggleRow
                                    item={{
                                        label: 'Disable Student Invitations',
                                        name: 'disableStudentInvitations'
                                    }}
                                    checked={getItemChecked({
                                        name: 'disableStudentInvitations'
                                    }, value)}
                                    onChange={(nextChecked) => {
                                        const nextValue = {
                                            ...value,
                                            disableStudentInvitations: nextChecked
                                        };
                                        setInvitationSettings(nextValue);
                                        onChange(nextValue);
                                    }}
                                />
                            </Box>
                            <Box>
                                <StudentParentInvitationsSettings
                                    schoolId={schoolInfo.id}
                                    value={parentInvitationSettings}
                                    embedded
                                    onChange={(nextValue) => {
                                        setParentInvitationSettings(nextValue);
                                    }}
                                />
                            </Box>
                            <Box>
                                <SettingsToggleRow
                                    item={{
                                        label: 'Disable Employee Invitations',
                                        name: 'disableEmployeeInvitations'
                                    }}
                                    checked={getItemChecked({
                                        name: 'disableEmployeeInvitations'
                                    }, value)}
                                    onChange={(nextChecked) => {
                                        const nextValue = {
                                            ...value,
                                            disableEmployeeInvitations: nextChecked
                                        };
                                        setInvitationSettings(nextValue);
                                        onChange(nextValue);
                                    }}
                                />
                            </Box>
                        </Stack>
                    </Box>
                </Box>;
            }
        },
        {
            name: 'notificationSettings',
            initialValue: notificationSettings,
            render: props => {
                // eslint-disable-next-line react/prop-types
                const {onChange, value} = props;

                return <SettingsToggleSection
                    key="notificationSettings"
                    title="Notifications"
                    value={value}
                    onChange={(nextValue) => {
                        setNotificationSettings(nextValue);
                        onChange(nextValue);
                    }}
                    items={[
                        {
                            label: 'Disable Parent Notifications for Application Profile Updates',
                            name: 'disableParentNotificationApplicationUpdates'
                        },
                        {
                            label: 'Disable Parent Notifications for Student Profile Updates',
                            name: 'disableParentNotificationStudentUpdates'
                        }
                    ]}
                />;
            }
        },
        {
            name: 'mfaSettings',
            initialValue: mfaSettings,
            render: props => {
                // eslint-disable-next-line react/prop-types
                const {onChange, value} = props;

                return <Box key="mfaSettings" sx={{mt: 2, mb: 1}}>
                    <Typography variant="h6" fontWeight="bold" sx={{mb: 1}}>
                        Multi-factor authentication
                    </Typography>
                    <Box sx={settingsSectionSx}>
                        <Stack spacing={2} divider={<Divider flexItem />}>
                            <SettingsToggleRow
                                item={{
                                    label: 'Require email verification code on login',
                                    name: 'mfaEnabled',
                                    description: 'Users will receive a one-time code by email after entering their password.'
                                }}
                                checked={getItemChecked({name: 'mfaEnabled'}, value)}
                                onChange={(nextChecked) => {
                                    const nextValue = {
                                        ...value,
                                        mfaEnabled: nextChecked
                                    };
                                    setMfaSettings(nextValue);
                                    onChange(nextValue);
                                }}
                            />
                            <Box>
                                <Typography fontWeight={600}>Trusted device period (days)</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                                    How long a verified device can skip MFA. Use 0 to always require a code.
                                </Typography>
                                <Box
                                    component="input"
                                    type="number"
                                    min={0}
                                    max={365}
                                    // eslint-disable-next-line react/prop-types
                                    value={value?.mfaDeviceTrustDays ?? 30}
                                    onChange={(event) => {
                                        const nextValue = {
                                            ...value,
                                            mfaDeviceTrustDays: event.target.value
                                        };
                                        setMfaSettings(nextValue);
                                        onChange(nextValue);
                                    }}
                                    sx={{
                                        width: 120,
                                        p: 1,
                                        border: '1px solid rgba(0, 0, 0, 0.23)',
                                        borderRadius: 1
                                    }}
                                />
                            </Box>
                        </Stack>
                    </Box>
                </Box>;
            }
        },
        {
            name: 'medicineSettings',
            initialValue: medicineSettings,
            render: props => {
                // eslint-disable-next-line react/prop-types
                const {onChange, value} = props;

                return <Box key="medicineSettings" sx={{mt: 2, mb: 1}}>
                    <Typography variant="h6" fontWeight="bold" sx={{mb: 1}}>
                        Medicine at school
                    </Typography>
                    <Box sx={settingsSectionSx}>
                        <Box>
                            <Typography fontWeight={600}>Warn this many days before a medicine expires</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                                The school and the child&apos;s contacts are emailed once a box comes within this many days of its expiry date.
                            </Typography>
                            <Box
                                component="input"
                                type="number"
                                min={1}
                                max={365}
                                // eslint-disable-next-line react/prop-types
                                value={value?.medicineExpiryWarningDays ?? 28}
                                onChange={(event) => {
                                    const nextValue = {
                                        ...value,
                                        medicineExpiryWarningDays: event.target.value
                                    };
                                    setMedicineSettings(nextValue);
                                    onChange(nextValue);
                                }}
                                sx={{
                                    width: 120,
                                    p: 1,
                                    border: '1px solid rgba(0, 0, 0, 0.23)',
                                    borderRadius: 1
                                }}
                            />
                        </Box>
                    </Box>
                </Box>;
            }
        }
    ], [groupSettings, invitationSettings, notificationSettings, mfaSettings, medicineSettings, user, schoolInfo.id, parentInvitationSettings]);

    const customFields = useMemo(() =>
        [
            {
                name: 'logo',
                initialValue: currentLogo,
                render: props => {
                    // eslint-disable-next-line react/prop-types
                    const {onChange, ...restProps} = props;

                    return <Box key="logo" sx={{mt: 2, mb: 1}}>
                        <Typography variant="h6" fontWeight="bold" sx={{mb: 1}}>
                            Logo
                        </Typography>
                        <Box sx={settingsSectionSx}>
                            <LogoUploader
                                {...restProps}
                                onChange={(nextValue) => {
                                    setCurrentLogo(nextValue);
                                    onChange(nextValue);
                                }}
                            />
                        </Box>
                    </Box>;
                }
            }
        ], [currentLogo]);

    const syncFields = useMemo(() =>
        [
            {
                name: 'syncInterval',
                initialValue: schoolInfo.syncInterval,
                render: (props) => {
                    // eslint-disable-next-line react/prop-types
                    const { onChange } = props;
                    return (
                        <Box key="syncInterval" sx={{mt: 2, mb: 1}}>
                            <Typography variant="h6" fontWeight="bold" sx={{mb: 1}}>
                                Sync
                            </Typography>
                            <Box sx={settingsSectionSx}>
                                <AdmicityCronScheduler
                                    value={schoolInfo.syncInterval}
                                    onChange={(cron) => {
                                        setCronExpression(cron);
                                        if (onChange) {
                                            onChange(cron);
                                        }
                                    }}
                                    label="Sync"
                                />
                            </Box>
                        </Box>
                    );
                }
            },
            {
                name: 'syncPhotoInterval',
                initialValue: schoolInfo.syncPhotoInterval,
                render: (props) => {
                    // eslint-disable-next-line react/prop-types
                    const { onChange } = props;
                    return (
                        <Box key="syncPhotoInterval" sx={{mb: 1}}>
                            <Box sx={settingsSectionSx}>
                                <AdmicityCronScheduler
                                    value={schoolInfo.syncPhotoInterval}
                                    onChange={(cron) => {
                                        setPhotoCronExpression(cron);
                                        if (onChange) {
                                            onChange(cron);
                                        }
                                    }}
                                    label="Photo Sync"
                                />
                            </Box>
                        </Box>
                    );
                }
            }
        ], [schoolInfo.syncInterval, schoolInfo.syncPhotoInterval]);

    return (
        <Box
            display="flex"
            flexDirection="column"
            sx={{
                mt: 2,
                width: {
                    xs: '100%',
                },
                maxWidth: 1200,
                mx: 'auto',
                position: 'relative',
            }}
        >
            <Typography sx={{marginBottom: 2}} variant="h6" fontWeight="bold">General School Settings</Typography>
            {
                showAlert
                    ? <Alert
                        sx={{marginBottom: 2}}
                        severity="info"
                        onClose={() => setShowAlert(false)}
                    >
                        Changes to the &quot;Hide House Groups&quot; or &quot;Hide Border Groups&quot; groups will take
                        effect during the next
                        synchronization, scheduled to occur in 1 hour.
                    </Alert>
                    : ''
            }
            <AdmicityForm
                textFields={textFields}
                custom={[
                    toggleSections[0],
                    toggleSections[1],
                    toggleSections[2],
                    toggleSections[3],
                    toggleSections[4],
                    ...(user.hasRole(ROLES.SCHOOL_ADMIN) ? customFields : []),
                    ...syncFields
                ]}
                handleSubmit={handleSubmit}
                buttonText="Save"
                setIsChildFormValid={setIsFormValid}
                actionsContainerProps={{
                    container: {
                        justifyContent: 'flex-end'
                    },
                    button: {
                        fullWidth: false,
                        disabled: isLoading || !isFormValid
                    }
                }}
            />
            <AdmicityDialog
                open={confirmOpen}
                handleClose={handleCancelSave}
                title="Confirm Save"
                contentText="Are you sure you want to save these school setting changes?"
                actions={[
                    {
                        label: 'Cancel',
                        onClick: handleCancelSave
                    },
                    {
                        label: 'Save',
                        onClick: handleConfirmSave
                    }
                ]}
            >
                {pendingInvitationWarning && (
                    <DialogContentText sx={{mt: 2, mb: 0}}>
                        Saving these account registration invitation changes will send invitations to all users and contacts who are eligible to receive them
                        {pendingEnabledInvitationLabels.length > 0 && (
                            <>
                                {': '}
                                {pendingEnabledInvitationLabels.map((label, index) => (
                                    <Box
                                        key={label}
                                        component="span"
                                        sx={{fontWeight: 700}}
                                    >
                                        {index > 0 ? ', ' : ''}
                                        {label}
                                    </Box>
                                ))}
                                .
                            </>
                        )}
                    </DialogContentText>
                )}
            </AdmicityDialog>
        </Box>
    );
};

SchoolSetupForm.defaultProps = {
    editMode: false
};

SchoolSetupForm.propTypes = {
    editMode: PropTypes.bool,
    schoolInfo: PropTypes.object,
    logo: PropTypes.object,
    onSubmit: PropTypes.func,
    isLoading: PropTypes.bool
};

export default SchoolSetupForm;
